import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Tag } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
export const tagKeys = {
  all: ["tags"] as const,
  lists: () => [...tagKeys.all, "list"] as const,
};

// Hooks
export function useTags() {
  return useQuery({
    queryKey: tagKeys.lists(),
    queryFn: () => apiClient.getTags(),
    staleTime: 1000 * 60 * 15, // Tags rarely change, cache for 15 min
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => apiClient.createTag({ name }),
    onMutate: async (name) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: tagKeys.lists() });

      // Snapshot previous value
      const previousTags = queryClient.getQueryData<Tag[]>(tagKeys.lists());

      // Optimistically add new tag (with temporary ID)
      if (previousTags) {
        const tempTag: Tag = {
          id: Date.now(), // Temporary ID
          name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        queryClient.setQueryData<Tag[]>(tagKeys.lists(), [...previousTags, tempTag]);
      }

      return { previousTags };
    },
    onError: (error: Error, name, context) => {
      // Rollback on error
      if (context?.previousTags) {
        queryClient.setQueryData(tagKeys.lists(), context.previousTags);
      }

      toast({
        title: "Error",
        description: error.message || "Failed to create tag",
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Refetch to get real data
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
}
